import React, { useState, useRef, useEffect } from 'react';
import { ShoppingCart, Package, ChevronDown } from 'lucide-react';
import { mockProducts } from '../../data/mockData';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const BuySell = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [formData, setFormData] = useState({
    programName: '',
    seller: '',
    purchaseCount: '',
    price: ''
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter products based on search term
  const filteredProducts = mockProducts.filter(product =>
    product.programName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProgramNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, programName: value }));
    setSearchTerm(value);
    setIsDropdownOpen(true);
  };

  const handleProductSelect = (product) => {
    setFormData(prev => ({
      ...prev,
      programName: product.programName,
      seller: product.seller,
      price: product.price.toString()
    }));
    setSearchTerm(product.programName);
    setIsDropdownOpen(false);
  };

  const handleProductRowClick = (product) => {
    handleProductSelect(product);
    // Scroll to form section
    const formSection = document.querySelector('.transaction-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Buy/Sell transaction:', formData);
    toast.success(t('buySell.transactionSuccess'));
    setFormData({ programName: '', seller: '', purchaseCount: '', price: '' });
  };

  return (
    <div className={`max-w-6xl mx-auto p-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
        {t('buySell.title')}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 transaction-form">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              {t('buySell.newTransaction')}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('buySell.programName')} *
                </label>
                <div className="relative">
                  <input
                    ref={inputRef}
                    type="text"
                    name="programName"
                    value={formData.programName}
                    onChange={handleProgramNameChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    className={`w-full px-4 py-2 ${isRTL ? 'pl-10 pr-4' : 'pr-10 pl-4'} border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('buySell.programNamePlaceholder')}
                    required
                    autoComplete="off"
                  />
                  <ChevronDown 
                    className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                    size={20}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  />
                </div>
                {isDropdownOpen && filteredProducts.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                        onClick={() => handleProductSelect(product)}
                      >
                        <div className={`font-medium text-gray-900 dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}>
                          {product.programName}
                        </div>
                        <div className={`text-sm text-gray-500 dark:text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
                          {product.seller} • {product.price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('buySell.seller')} *
                </label>
                <input
                  type="text"
                  name="seller"
                  value={formData.seller}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'}`}
                  placeholder={t('buySell.sellerPlaceholder')}
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('buySell.purchaseCount')} *
                  </label>
                  <input
                    type="number"
                    name="purchaseCount"
                    value={formData.purchaseCount}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('buySell.purchaseCountPlaceholder')}
                    min="1"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('buySell.price')} *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      className={`w-full ${isRTL ? 'pr-8 pl-4' : 'pl-8 pr-4'} py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isRTL ? 'text-right' : 'text-left'}`}
                      placeholder={t('buySell.pricePlaceholder')}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>
              </div>
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium rounded-lg focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors"
              >
                <ShoppingCart size={20} className={isRTL ? 'ml-2' : 'mr-2'} />
                {t('buySell.recordTransaction')}
              </button>
            </form>
          </div>
        </div>

        <div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
              <Package className={isRTL ? 'ml-2' : 'mr-2'} size={24} />
              {t('buySell.availableProducts')}
            </h2>
            <div className="app-table-wrap">
              <table className="app-table min-w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-600">
                    <th className={`py-3 px-4 font-medium text-gray-700 dark:text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`}>{t('buySell.program')}</th>
                    <th className={`py-3 px-4 font-medium text-gray-700 dark:text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`}>{t('buySell.seller')}</th>
                    <th className={`py-3 px-4 font-medium text-gray-700 dark:text-gray-300 ${isRTL ? 'text-left' : 'text-right'}`}>{t('buySell.price')}</th>
                    <th className={`py-3 px-4 font-medium text-gray-700 dark:text-gray-300 text-center`}>{t('buySell.available')}</th>
                  </tr>
                </thead>
                <tbody>
                    {mockProducts.map((product) => (
                      <tr 
                        key={product.id} 
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors cursor-pointer group"
                        onClick={() => handleProductRowClick(product)}
                        title={t('buySell.clickToSelect', { program: product.programName })}
                      >
                        <td className={`py-3 px-4 font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 ${isRTL ? 'text-right' : 'text-left'}`}>{product.programName}</td>
                        <td className={`py-3 px-4 text-gray-600 dark:text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`}>{product.seller}</td>
                        <td className={`py-3 px-4 font-medium text-gray-900 dark:text-white ${isRTL ? 'text-left' : 'text-right'}`}>{product.price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            product.availableCount > 20 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }`}>
                            {product.availableCount}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuySell;